import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../../hooks/useAppStore';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v2';

export interface MensajeHistorial {
  role: 'user' | 'assistant';
  content: string;
  channel?: 'crm' | 'whatsapp';
  createdAt: string;
}

interface Pagina {
  messages: MensajeHistorial[];
  total:    number;
  hasMore:  boolean;
}

/**
 * EL HISTORIAL DEL HILO, DE A PÁGINAS.
 *
 * (25/08/2026, pedido del user) El hilo de Gustavo es uno solo por agente, compartido con
 * WhatsApp, y crece para siempre: traerlo entero en cada apertura del widget era el request
 * más pesado del CRM y encima se paga varias veces por día. Acá se pide solo la última
 * página, y las anteriores recién cuando el agente scrollea hacia arriba.
 *
 * `antesDe` (el `before` del backend) es cuántos mensajes YA tiene el cliente contados desde
 * el final. Es lo que hace que la paginación no se corra cuando llega un mensaje nuevo
 * mientras se está mirando el historial: con un offset desde el principio, cada respuesta de
 * Gustavo movía la ventana y la página siguiente repetía o se salteaba mensajes.
 */
export function useHistorialGustavo(conversationId?: string, opciones?: { activo?: boolean }) {
  const activo = opciones?.activo ?? true;
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  /**
   * Los mensajes VIENEN ETIQUETADOS con el hilo al que pertenecen.
   *
   * Sin la etiqueta, el frame siguiente a elegir otra conversación mostraba los mensajes de
   * la anterior: el estado se limpia dentro del efecto, que corre DESPUÉS de pintar. Con el
   * hilo al lado, el descarte se resuelve en el render y no llega a verse.
   */
  const [datos, setDatos] = useState<{ para: string; mensajes: MensajeHistorial[] } | undefined>(undefined);
  const [hayMas, setHayMas] = useState(false);
  /**
   * Cuántos mensajes del hilo quedaron ARRIBA de lo cargado. Lo consume el chat para que el
   * índice del 👍/👎 siga siendo el del hilo entero y no el de la lista que tiene a mano.
   */
  const [offset, setOffset] = useState(0);
  /** Cuántos tiene el cliente: NO es `mensajes.length` — lo que se envía en vivo no cuenta. */
  const tieneRef = useRef(0);

  const pedir = useCallback(async (antesDe: number, signal?: AbortSignal): Promise<Pagina | null> => {
    if (!conversationId || !accessToken) return null;
    const res = await fetch(
      `${BASE_URL}/ai/conversations/${conversationId}?before=${antesDe}`,
      { credentials: 'include', headers: { Authorization: `Bearer ${accessToken}` }, signal },
    );
    if (!res.ok) throw new Error(String(res.status));
    const json = await res.json();
    const d = json?.data ?? {};
    return { messages: d.messages ?? [], total: d.total ?? 0, hasMore: !!d.hasMore };
  }, [conversationId, accessToken]);

  /**
   * ⛔ CADA APERTURA TRAE LA CHARLA DE NUEVO. (pedido del user, 24/09/2026)
   *
   * Al reabrir el widget quedaba lo cargado la vez anterior: `listo` daba verdadero al
   * instante, el chat se armaba con esos mensajes y la página nueva —con lo que entró por
   * WhatsApp o los avisos de Gustavo mientras estaba cerrado— llegaba cuando el chat ya había
   * tomado los viejos (los toma UNA vez, al montarse). Había que recargar la página.
   * Al pasar de cerrado a abierto se descarta lo guardado y se muestra el loader hasta que
   * llega la charla actual. Solo en la apertura: una renovación de token con el chat abierto
   * no puede desarmar una conversación en curso.
   */
  const estabaActivo = useRef(false);
  useEffect(() => {
    if (activo && !estabaActivo.current) setDatos(undefined);
    estabaActivo.current = activo;
  }, [activo]);

  // Primera página: la última parte del hilo, que es lo que el agente quiere ver al abrir.
  useEffect(() => {
    if (!activo || !conversationId || !accessToken) return;
    const ac = new AbortController();
    let vivo = true;
    tieneRef.current = 0;
    (async () => {
      try {
        const p = await pedir(0, ac.signal);
        if (!vivo || !p) return;
        setDatos({ para: conversationId, mensajes: p.messages });
        setHayMas(p.hasMore);
        setOffset(Math.max(p.total - p.messages.length, 0));
        tieneRef.current = p.messages.length;
      } catch (e) {
        // Abortar al cambiar de hilo no es una falla: solo dejamos de esperar esta respuesta.
        if (!vivo || (e as Error).name === 'AbortError') return;
        // Un hilo que no existe (borrado desde otra pantalla) no puede dejar el chat colgado
        // en el loader: se lo trata como vacío y el agente puede escribir igual.
        setDatos({ para: conversationId, mensajes: [] });
        setHayMas(false);
        setOffset(0);
      }
    })();
    return () => { vivo = false; ac.abort(); };
  }, [activo, conversationId, accessToken, pedir]);

  const [cargandoMas, setCargandoMas] = useState(false);
  const cargandoMasRef = useRef(false);

  /**
   * La página anterior. Devuelve los mensajes para que los ponga arriba quien los muestra
   * —el chat los mezcla con lo que se está escribiendo en vivo, que este hook no conoce—.
   */
  const cargarMas = useCallback(async (): Promise<MensajeHistorial[]> => {
    // El guard es un ref y no el estado: el scroll dispara varias veces seguidas y con el
    // estado (que se actualiza recién en el próximo render) entraban tres pedidos iguales.
    if (cargandoMasRef.current || !hayMas) return [];
    cargandoMasRef.current = true;
    setCargandoMas(true);
    try {
      const p = await pedir(tieneRef.current);
      if (!p) return [];
      tieneRef.current += p.messages.length;
      setHayMas(p.hasMore);
      setOffset(Math.max(p.total - tieneRef.current, 0));
      return p.messages;
    } catch {
      // Sin conexión: no se apaga `hayMas`, así que el próximo scroll vuelve a intentar.
      return [];
    } finally {
      cargandoMasRef.current = false;
      setCargandoMas(false);
    }
  }, [hayMas, pedir]);

  // Lo cargado sirve solo si es de ESTE hilo; si no, todavía se está trayendo.
  const listo = !!conversationId && datos?.para === conversationId;
  return {
    mensajes: listo ? datos!.mensajes : undefined,
    /** Hay un hilo pedido y todavía no llegó: es el loader del modal entero. */
    cargandoInicial: !!conversationId && !listo,
    cargandoMas, hayMas, offset, cargarMas,
  };
}
